import { AlertTriangle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/ui-components/Dialog'
import type { OrganizationUser } from '@/views/Users/types'

type RemoveUserModalProps = {
  isOpen: boolean
  onClose: () => void
  user: OrganizationUser | null
  onSubmit: (userId: string) => void
}

const RemoveUserModal = ({ isOpen, onClose, user, onSubmit }: RemoveUserModalProps) => {
  const [isRemoving, setIsRemoving] = useState(false)

  useEffect(() => {
    setIsRemoving(false)
  }, [isOpen])

  const handleSubmit = async () => {
    if (!user) {
      return
    }

    setIsRemoving(true)
    onSubmit(user.id)
  }

  const handleCancel = () => {
    setIsRemoving(false)
    onClose()
  }

  if (!user) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-red-600 dark:text-red-400" />
          <DialogTitle>Remove User</DialogTitle>
        </div>
        <Button
          plain
          onClick={handleCancel}
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-200">
            Are you sure you want to remove <strong>{user.name}</strong> ({user.email}) from this organization?
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This action cannot be undone. The user will lose access to all organization resources.
          </p>
        </div>
      </DialogBody>

      <DialogActions>
        <Button
          plain
          onClick={handleCancel}
          disabled={isRemoving}
        >
          Cancel
        </Button>
        <Button
          color="red"
          className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
          onClick={handleSubmit}
          disabled={isRemoving}
          data-testid="remove-user-confirm"
        >
          {isRemoving ? 'Removing...' : 'Remove User'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RemoveUserModal
